import React, { useState, useEffect } from 'react';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import {
  IconButton, Container, Typography, Box, Paper, Tooltip, Dialog, DialogTitle, DialogContent, DialogContentText,
  DialogActions, Button, FormControl, Grid, TextField, Select, MenuItem, InputLabel, useMediaQuery, useTheme,
  Card, CardContent, CardActions, Chip, Stack, FormHelperText, Avatar
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import HttpsIcon from '@mui/icons-material/Https';
import CircularProgress from '@mui/material/CircularProgress';
import Swal from 'sweetalert2';
import { Tag, message } from 'antd';
import { useFormik } from 'formik';
import * as Yup from 'yup';

const colors = ['magenta', 'orange', 'gold', 'lime', 'cyan', 'blue', 'geekblue', 'purple', 'yellow', "gray"];

function getRandomColor() {
  const randomIndex = Math.floor(Math.random() * colors.length);
  return colors[randomIndex];
}

const safeParseFloat = (value, defaultValue = 0) => {
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    const parsed = parseFloat(value.replace(/,/g, ''));
    return isNaN(parsed) ? defaultValue : parsed;
  }
  return defaultValue;
};

function ModifyServices() {
  const [services, setServices] = useState([]);
  const [serviceTypeColors, setServiceTypeColors] = useState({});
  const [messageApi, contextHolder] = message.useMessage();
  const [selectedService, setSelectedService] = useState(null);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [serviceTypes, setServiceTypes] = useState([]);
  const [permissionError, setPermissionError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Responsive
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const token = localStorage.getItem('token');

  const getServiceTypes = async () => {
    try {
      const response = await fetch('https://dprincess-training.servehttp.com/serviceTypes-list', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      if (data.error) {
        console.error(data.error);
        setServiceTypes([{ serviceType: 'Error al cargar tipos de servicio, refresca la página' }]);
      } else {
        const addServiceType = { id: 'agregar', serviceType: 'Agregar nuevo tipo de servicio' };
        setServiceTypes([...data, addServiceType]);
      }
    } catch (error) {
      console.error('Error:', error);
      messageApi.error('Error al cargar los tipos de servicio. Por favor, recargue la página.');
    }
  };

  const getServices = async () => {
    try {
      const response = await fetch('https://dprincess-training.servehttp.com/services-list', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      if (data.error) {
        console.error(data.error);
      } else {
        setServices(data);
        assignColorsToServiceTypes(data);
      }
    } catch (error) {
      console.error('Error:', error);
      messageApi.error('Error al cargar los servicios. Por favor, recargue la pagina.');
    }
  };

  const checkPermissions = async () => {
    if (!token) {
      messageApi.error('No se encontró el token de autenticación.');
      setPermissionError(true);
      window.location.href = '/sign-in'; // Redirect to login if no token
      return;
    }
    setPermissionError(false);
    setIsLoading(true);
    try {
      const response = await fetch('https://dprincess-training.servehttp.com/users/check-permissions', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.status === 403) {
        messageApi.error('No tienes permisos para acceder a esta sección.');
        setPermissionError(true);
        throw new Error('No tienes permisos para acceder a esta sección.');
      }
      else if (response.ok) {
        getServices();
        getServiceTypes();
      }
    } catch (error) {
      messageApi.error(error.message || 'Error al verificar permisos.');
      throw error; // Re-throw to handle it in the calling function
    } finally {
      setIsLoading(false);
    }
  };

  const fetchModifyService = async (serviceID, values) => {
    try {
      const confirm = await Swal.fire({
        title: 'Modificar Servicio',
        text: '¿Estás seguro/a de que quieres modificar este servicio?',
        icon: 'warning',
        showCancelButton: true,
        cancelButtonColor: '#d33',
        cancelButtonText: 'Cancelar',
        confirmButtonText: 'Modificar',
        confirmButtonColor: '#4caf50'
      });
      if (confirm.isConfirmed) {
        const response = await fetch(`https://dprincess-training.servehttp.com/service/update-service/${serviceID}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(values)
        });
        if (response.ok) {
          const data = await response.json();
          if (data.error) {
            messageApi.error(data.error);
          } else {
            messageApi.success('Servicio modificado exitosamente.');
            getServices().then(() => setOpenEditDialog(false));
          }
        }
        else {
          const errorData = await response.json();
          messageApi.error(errorData.error || 'Error al modificar el servicio.');
        }
      }
    } catch (error) {
      console.error('Error:', error);
      messageApi.error('Error al modificar el servicio. Por favor, intenta de nuevo.');
    }
  };

  const assignColorsToServiceTypes = (services) => {
    const colorsMap = {};
    services.forEach(service => {
      if (!colorsMap[service.serviceType]) {
        colorsMap[service.serviceType] = getRandomColor();
      }
    });
    setServiceTypeColors(colorsMap);
  };

  const handleEdit = (service) => {
    setSelectedService(service);
    setOpenEditDialog(true);
  }

  const handleDelete = async (serviceID) => {
    const confirmReq = await Swal.fire({
      title: 'Confirmar eliminación',
      text: '¿Estás seguro/a de que quieres eliminar este servicio?',
      icon: 'warning',
      showCancelButton: true,
      cancelButtonColor: '#d33',
      cancelButtonText: 'Cancelar',
      confirmButtonText: 'Eliminar',
      confirmButtonColor: '#4caf50'
    });
    if (confirmReq.isConfirmed) {
      try {
        const response = await fetch(`https://dprincess-training.servehttp.com/delete-service/${serviceID}`, {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        });
        const data = await response.json();
        if (data.error) {
          Swal.fire({
            title: 'Error al eliminar servicio',
            text: 'Ha ocurrido un error al eliminar el servicio, por favor intenta de nuevo.',
            icon: 'error',
            confirmButtonText: 'Entendido',
            confirmButtonColor: '#f44336'
          });
        } else {
          Swal.fire({
            title: 'Servicio eliminado',
            text: 'El servicio ha sido eliminado exitosamente.',
            icon: 'success',
            confirmButtonText: 'Entendido',
            confirmButtonColor: '#4caf50'
          });
          getServices();
        }
      } catch (error) {
        console.error('Error:', error);
        Swal.fire({
          title: 'Error',
          text: 'Ha ocurrido un error inesperado.',
          icon: 'error',
          confirmButtonText: 'Entendido',
          confirmButtonColor: '#f44336'
        });
      }
    }
  };

  // Componente para vista móvil
  const MobileServiceCard = ({ service }) => (
    <Card
      sx={{
        mb: 2,
        border: '1px solid #f759ab',
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(247, 89, 171, 0.1)',
        '&:hover': {
          boxShadow: '0 4px 16px rgba(247, 89, 171, 0.2)',
        }
      }}
    >
      <CardContent sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Typography variant="h6" sx={{ color: '#4b0082', fontWeight: 600, flex: 1, pr: 1 }}>
            {service.serviceName}
          </Typography>
          <Chip
            label={`ID: ${service.serviceID}`}
            size="small"
            sx={{ bgcolor: '#f3e8ff', color: '#4b0082' }}
          />
        </Box>

        <Stack spacing={1.5}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2" sx={{ color: '#666' }}>
              Precio:
            </Typography>
            <Typography variant="h6" sx={{ color: '#f759ab', fontWeight: 600 }}>
              {safeParseFloat(service.servicePrice).toLocaleString('es-DO', {
                style: 'currency',
                currency: 'DOP'
              })}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2" sx={{ color: '#666' }}>
              Tipo:
            </Typography>
            <Tag color={serviceTypeColors[service.serviceType] || getRandomColor()}>
              {service.serviceType}
            </Tag>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2" sx={{ color: '#666' }}>
              Estado:
            </Typography>
            <Tag color={service.status === 'activo' ? 'green' : 'volcano'}>
              {service.status ? service.status.toUpperCase() : 'N/A'}
            </Tag>
          </Box>
        </Stack>
      </CardContent>

      <CardActions sx={{ justifyContent: 'center', pt: 0, pb: 2 }}>
        <Button
          variant="outlined"
          startIcon={<EditIcon />}
          onClick={() => handleEdit(service)}
          sx={{
            color: '#f759ab',
            borderColor: '#f759ab',
            '&:hover': {
              backgroundColor: 'rgba(247, 89, 171, 0.1)',
              borderColor: '#f759ab',
            },
            mr: 1
          }}
          size="small"
        >
          Editar
        </Button>
        <Button
          variant="outlined"
          startIcon={<DeleteIcon />}
          onClick={() => handleDelete(service.serviceID)}
          sx={{
            color: '#d32f2f',
            borderColor: '#d32f2f',
            '&:hover': {
              backgroundColor: 'rgba(211, 47, 47, 0.1)',
              borderColor: '#d32f2f',
            }
          }}
          size="small"
        >
          Eliminar
        </Button>
      </CardActions>
    </Card>
  );

  useEffect(() => {
    checkPermissions();
  }, []);

  const columns = [
    { field: 'serviceID', headerName: 'ID', flex: 0.5 },
    { field: 'serviceName', headerName: 'Nombre del Servicio', flex: 2.5 },
    {
      field: 'servicePrice',
      headerName: 'Precio',
      flex: 1,
      valueFormatter: (value) => {
        const number = safeParseFloat(value);
        return isNaN(number) ? '' : number.toLocaleString('es-DO', { style: 'currency', currency: 'DOP' }); // Or 'es-DO' and 'DOP' if needed
      }
    },
    {
      field: 'serviceType',
      headerName: 'Tipo',
      flex: 1,
      renderCell: (params) => (
        <Tag color={serviceTypeColors[params.value] || getRandomColor()}>{params.value}</Tag>
      ),
    },
    {
      field: 'status',
      headerName: 'Estado',
      flex: 1,
      renderCell: (params) => (
        <Tag color={params.value === 'activo' ? 'green' : 'volcano'}>
          {params.value ? params.value.toUpperCase() : 'N/A'}
        </Tag>
      ),
    },
    {
      field: 'actions',
      headerName: 'Acciones',
      flex: 1,
      sortable: false, // Actions column shouldn't be sortable
      filterable: false, // Actions column shouldn't be filterable
      renderCell: (params) => (
        <Box> {/* Use Box for better layout control */}
          <Tooltip title="Editar" arrow>
            <IconButton color="primary" onClick={() => handleEdit(params.row)}>
              <EditIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Eliminar" arrow>
            {/* Pass serviceID to handleDelete */}
            <IconButton color="error" onClick={() => handleDelete(params.row.serviceID)}>
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <CircularProgress />
      </Box>
    );
  }

  // Mostrar mensaje de falta de permisos si corresponde
  if (permissionError) {
    return (
      <Container maxWidth="sm" sx={{ py: 8 }}>
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '60vh',
          gap: 2
        }}>
          <Avatar sx={{ bgcolor: '#f44336', width: 64, height: 64 }}>
            {/* Icono de candado para indicar acceso restringido */}
            <HttpsIcon sx={{ fontSize: 40 }} />
          </Avatar>
          <Typography variant="h5" sx={{ color: '#f44336', fontWeight: 700 }}>
            No tienes permiso para acceder a esta sección
          </Typography>
          <Typography variant="body1" sx={{ color: '#666', textAlign: 'center' }}>
            Esta información solo está disponible para usuarios con permisos de administrador.
          </Typography>
        </Box>
      </Container>
    );
  };

  return (
    <Container maxWidth="lg" sx={{ px: { xs: 1, sm: 2, md: 3 } }}>
      {contextHolder}
      <Paper elevation={3} sx={{ p: { xs: 2, sm: 3, md: 4 }, mt: { xs: 2, md: 4 }, mb: { xs: 2, md: 4 } }}>
        <Typography
          variant={isMobile ? "h5" : "h4"}
          component="h1"
          gutterBottom
          align="center"
          sx={{
            color: '#531dab',
            fontWeight: 'bold',
            marginBottom: { xs: '16px', md: '20px' },
            textTransform: 'uppercase',
            letterSpacing: { xs: '1px', md: '2px' },
            fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' }
          }}
        >
          Modificar Servicios
        </Typography>

        {/* Vista móvil - Tarjetas */}
        {isMobile ? (
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" sx={{ color: '#666', mb: 2, textAlign: 'center' }}>
              {services.length} servicio{services.length !== 1 ? 's' : ''} encontrado{services.length !== 1 ? 's' : ''}
            </Typography>
            {services.length > 0 ? (
              services.map((service) => (
                <MobileServiceCard key={service.serviceID} service={service} />
              ))
            ) : (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="h6" sx={{ color: '#4b0082', mb: 1 }}>
                  No hay servicios registrados
                </Typography>
                <Typography variant="body2" sx={{ color: '#666' }}>
                  Los servicios aparecerán aquí una vez que se agreguen al sistema
                </Typography>
              </Box>
            )}
          </Box>
        ) : (
          /* Vista escritorio - DataGrid */
          <Box sx={{ height: { xs: 400, md: 600 }, width: '100%' }}>
            <DataGrid
              rows={services}
              columns={columns}
              disableSelectionOnClick
              getRowId={(row) => row.serviceID}
              sx={{
                '& .MuiDataGrid-cell': {
                  fontSize: { xs: '0.875rem', md: '1rem' }
                },
                '& .MuiDataGrid-columnHeaders': {
                  fontSize: { xs: '1rem', md: '1.2rem' },
                  backgroundColor: '#f759ab !important',
                  color: '#fff',
                  width: '100%'
                },
                '& .MuiDataGrid-columnHeader': {
                  backgroundColor: '#f759ab',
                  width: '100%'
                },
                '& .MuiDataGrid-columnHeadersInner': {
                  backgroundColor: '#f759ab',
                },
                '& .MuiDataGrid-scrollbarFiller': {
                  backgroundColor: '#f759ab',
                },
              }}
              slots={{
                toolbar: GridToolbar,
              }}
              slotProps={{
                toolbar: {
                  sx: {
                    backgroundColor: '#f759ab',
                    color: '#fff',
                    padding: '8px',
                    borderBottom: '1px solid #e0e0e0',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    '& .MuiButton-root': {
                      fontSize: { xs: '0.8rem', md: '0.9rem' },
                      textTransform: 'none',
                      color: '#fff',
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#fff',
                      },
                    },
                    '& .MuiInputBase-root': {
                      fontSize: { xs: '0.8rem', md: '0.9rem' },
                      padding: '4px 8px',
                      color: '#fff',
                      borderColor: '#fff',
                    },
                  },
                  showQuickFilter: true,
                  quickFilterProps: { debounceMs: 500 }
                },
              }}
              initialState={{
                sorting: {
                  sortModel: [{ field: 'serviceID', sort: 'asc' }],
                },
              }}
            />
          </Box>
        )}
      </Paper>
      <EditServiceDialog
        open={openEditDialog}
        onClose={() => {
          setOpenEditDialog(false);
          setSelectedService(null);
        }}
        service={selectedService}
        fetchModifyService={fetchModifyService}
        serviceTypes={serviceTypes}
        token={token}
        getServiceTypes={getServiceTypes}
        messageApi={messageApi}
      />
    </Container>
  );
}

export default ModifyServices;

const validationSchema = Yup.object({
  serviceName: Yup.string()
    .required('El nombre del servicio es requerido')
    .min(3, 'El nombre debe tener al menos 3 caracteres'),
  servicePrice: Yup.number()
    .required('El precio es requerido')
    .positive('El precio debe ser un número positivo')
    .typeError('El precio debe ser un número válido'), // Handle non-numeric input
  serviceTypeID: Yup.string() // Assuming serviceTypeID is the value for the Select
    .required('El tipo de servicio es requerido'),
  newServiceTypeName: Yup.string() // Validation for the new type name field
    .when('serviceTypeID', {
      is: 'agregar', // Only required if 'agregar' is selected
      then: (schema) => schema.required('Debe ingresar el nombre del nuevo tipo').min(3, 'El nombre debe tener al menos 3 caracteres'),
      otherwise: (schema) => schema.notRequired(),
    }),
  status: Yup.string()
    .required('El estado es requerido')
    .oneOf(['activo', 'inactivo'], 'Estado inválido'), // Ensure valid status
});

function EditServiceDialog({ open, onClose, service, fetchModifyService, serviceTypes, token, getServiceTypes, messageApi }) {
  const [showNewTypeField, setShowNewTypeField] = useState(false);

  // Responsive
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const addNewServiceType = async (typeName) => {
    try {
      const response = await fetch('https://dprincess-training.servehttp.com/serviceTypes/add-serviceType', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ serviceType: typeName })
      });
      const data = await response.json();
      if (response.ok && data.newServiceType) {
        messageApi.success(`Tipo de servicio "${typeName}" agregado.`);
        await getServiceTypes(); // Refresh the list of types
        return
      } else {
        messageApi.error(data.error || 'Error al agregar el tipo de servicio.');
        return null;
      }
    } catch (error) {
      console.error("Error adding service type:", error);
      messageApi.error('Error de conexión al agregar tipo de servicio.');
      return null;
    }
  };

  const formik = useFormik({
    initialValues: {
      serviceName: '',
      servicePrice: '',
      serviceTypeID: '', // Use serviceTypeID
      status: 'activo',
      newServiceTypeName: '', // Field for new type name
    },
    validationSchema: validationSchema,
    enableReinitialize: true, // Reinitialize form when 'service' prop changes
    onSubmit: async (values) => {
      let serviceTypeIDToSubmit = values.serviceTypeID;

      // If 'agregar' is selected, add the new type first
      if (values.serviceTypeID === 'agregar') {
        const newTypeId = await addNewServiceType(values.newServiceTypeName);
        if (newTypeId) {
          serviceTypeIDToSubmit = newTypeId; // Use the new ID
        } else {
          return; // Stop submission if adding type failed
        }
      }

      // Prepare data for API (exclude helper fields)
      const dataToSubmit = {
        serviceName: values.serviceName,
        servicePrice: values.servicePrice,
        serviceTypeID: serviceTypeIDToSubmit,
        status: values.status,
      };

      // Call the modification function passed via props
      await fetchModifyService(service?.serviceID, dataToSubmit);
      // fetchModifyService should handle closing the dialog on success
    },
  });

  // --- Effect to update Formik initial values when service prop changes ---
  useEffect(() => {
    if (service) {
      // Find the corresponding serviceTypeID based on service.serviceType name
      const typeMatch = serviceTypes.find(st => st.serviceType === service.serviceType);
      formik.setValues({
        serviceName: service.serviceName || '',
        servicePrice: service.servicePrice || '',
        serviceTypeID: typeMatch ? typeMatch.id : '', // Set the ID based on the match
        status: service.status || 'activo',
        newServiceTypeName: '', // Reset new type name field
      });
      setShowNewTypeField(false); // Hide new type field initially
    } else {
      // Reset form if service is null (e.g., dialog closed)
      formik.resetForm();
      setShowNewTypeField(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [service, serviceTypes]); // Rerun when service or serviceTypes change

  // --- Handle Service Type Change ---
  const handleServiceTypeChange = (event) => {
    const value = event.target.value;
    formik.setFieldValue('serviceTypeID', value);
    if (value === 'agregar') {
      setShowNewTypeField(true);
      formik.setFieldValue('newServiceTypeName', ''); // Clear potential previous value
    } else {
      setShowNewTypeField(false);
      formik.setFieldValue('newServiceTypeName', ''); // Clear it when switching away
    }
  };


  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      sx={{
        '& .MuiDialog-paper': {
          margin: isMobile ? 0 : '32px',
          borderRadius: isMobile ? 0 : '8px'
        }
      }}
    >
      <DialogTitle sx={{
        fontSize: { xs: '1.25rem', md: '1.5rem' },
        pb: { xs: 1, md: 2 }
      }}>
        Modificar Servicio
      </DialogTitle>
      {/* Use Formik's handleSubmit */}
      <form onSubmit={formik.handleSubmit}>
        <DialogContent sx={{ px: { xs: 2, md: 3 } }}>
          <DialogContentText sx={{ mb: { xs: 1, md: 2 }, fontSize: { xs: '0.875rem', md: '1rem' } }}>
            Modifica los detalles del servicio "{service?.serviceName}".
          </DialogContentText>
          <Grid container spacing={{ xs: 1.5, md: 2 }}>
            {/* Service Name */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                id="serviceName"
                name="serviceName"
                label="Nombre del Servicio"
                value={formik.values.serviceName}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.serviceName && Boolean(formik.errors.serviceName)}
                helperText={formik.touched.serviceName && formik.errors.serviceName}
                variant="outlined"
                size={isMobile ? "small" : "medium"}
              />
            </Grid>

            {/* Service Price */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                id="servicePrice"
                name="servicePrice"
                label="Precio"
                type="number"
                value={formik.values.servicePrice}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.servicePrice && Boolean(formik.errors.servicePrice)}
                helperText={formik.touched.servicePrice && formik.errors.servicePrice}
                variant="outlined"
                size={isMobile ? "small" : "medium"}
                InputProps={{
                  startAdornment: <Typography sx={{ mr: 0.5 }}>$</Typography>,
                }}
              />
            </Grid>

            {/* Service Type */}
            <Grid item xs={12}>
              <FormControl
                fullWidth
                error={formik.touched.serviceTypeID && Boolean(formik.errors.serviceTypeID)}
                size={isMobile ? "small" : "medium"}
              >
                <InputLabel id="service-type-label">Tipo de Servicio</InputLabel>
                <Select
                  labelId="service-type-label"
                  id="serviceTypeID"
                  name="serviceTypeID"
                  value={formik.values.serviceTypeID}
                  label="Tipo de Servicio"
                  onChange={handleServiceTypeChange}
                  onBlur={formik.handleBlur}
                  variant="outlined"
                >
                  <MenuItem value="" disabled>
                    <em>Selecciona un tipo</em>
                  </MenuItem>
                  {serviceTypes
                    .filter(type => type.id !== 'agregar')
                    .map((type) => (
                      <MenuItem key={type.id} value={type.id}>
                        {type.serviceType}
                      </MenuItem>
                    ))}
                  {serviceTypes.find(type => type.id === 'agregar') && (
                    <MenuItem key="agregar" value="agregar" sx={{ fontStyle: 'italic', color: 'primary.main' }}>
                      {serviceTypes.find(type => type.id === 'agregar').serviceType}
                    </MenuItem>
                  )}
                </Select>
                {formik.touched.serviceTypeID && formik.errors.serviceTypeID && (
                  <FormHelperText>{formik.errors.serviceTypeID}</FormHelperText>
                )}
              </FormControl>
            </Grid>

            {/* New Service Type Name (Conditional) */}
            {showNewTypeField && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  id="newServiceTypeName"
                  name="newServiceTypeName"
                  label="Nombre del Nuevo Tipo"
                  value={formik.values.newServiceTypeName}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.newServiceTypeName && Boolean(formik.errors.newServiceTypeName)}
                  helperText={formik.touched.newServiceTypeName && formik.errors.newServiceTypeName}
                  variant="outlined"
                  size={isMobile ? "small" : "medium"}
                  placeholder="Ej: Fotografía Adicional"
                />
              </Grid>
            )}

            {/* Status */}
            <Grid item xs={12}>
              <FormControl
                fullWidth
                error={formik.touched.status && Boolean(formik.errors.status)}
                size={isMobile ? "small" : "medium"}
              >
                <InputLabel id="status-label">Estado</InputLabel>
                <Select
                  labelId="status-label"
                  id="status"
                  name="status"
                  value={formik.values.status}
                  label="Estado"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  variant="outlined"
                >
                  <MenuItem value="activo">Activo</MenuItem>
                  <MenuItem value="inactivo">Inactivo</MenuItem>
                </Select>
                {formik.touched.status && formik.errors.status && (
                  <FormHelperText>{formik.errors.status}</FormHelperText>
                )}
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{
          padding: { xs: '12px 16px', md: '16px 24px' },
          flexDirection: { xs: 'column', sm: 'row' },
          gap: { xs: 1, sm: 0 }
        }}>
          <Button
            onClick={onClose}
            color="secondary"
            fullWidth={isMobile}
            sx={{ order: { xs: 2, sm: 1 } }}
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={formik.isSubmitting}
            fullWidth={isMobile}
            sx={{ order: { xs: 1, sm: 2 } }}
          >
            {formik.isSubmitting ? 'Guardando...' : 'Guardar Cambios'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}